import { type ComponentProps, useMemo } from "react";
import { Response } from "@strivelabs/ui-blocks";
import { CitationLink } from "./citation-link";
import { looksLikeDomain } from "./citation-favicon";

// Render citation links (bare-domain anchors) as favicon chips; all other
// markdown renders as usual. Stable identity so Response's memoization holds.
const CITATION_COMPONENTS = { a: CitationLink } as const;

// Models wrap inline citations in literal parentheses, e.g.
// `...impressions. ([justdial.com](https://justdial.com/...))`. Once the anchor
// becomes a chip the surrounding parens read as clutter, so strip the parens
// that wrap a lone domain-text link — matching how ChatGPT renders them.
// Capture group 1 = the link's visible text, group 2 = its URL.
const WRAPPED_CITATION_RE =
  /\(\s*\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)\s*\)/gi;

function stripCitationParens(markdown: string): string {
  return markdown.replace(WRAPPED_CITATION_RE, (match, text: string, url: string) =>
    looksLikeDomain(text) ? `[${text}](${url})` : match
  );
}

type ChatResponseProps = ComponentProps<typeof Response>;

/**
 * Chat-scoped markdown renderer: identical to `Response` but renders
 * model-emitted citation links as favicon chips. Kept separate from the shared
 * `Response` so report/rich-text rendering is unaffected.
 */
export function ChatResponse({ children, ...props }: ChatResponseProps) {
  // Streamed text re-renders often; only re-run the regex when it changes.
  const content = useMemo(
    () => (typeof children === "string" ? stripCitationParens(children) : children),
    [children]
  );

  return (
    <Response {...props} components={CITATION_COMPONENTS}>
      {content}
    </Response>
  );
}
